import React from 'react';

/**
 * 📊 EnterpriseTable — Standardized table component for all modules
 *
 * Matches the ExecutionGrid visual signature:
 *   - enterprise-table-shell container (border + radius + bg)
 *   - enterprise-table-header (sticky, uppercase, mono, muted)
 *   - enterprise-table-row (hover → surface-3, selected → surface-3 + left indicator)
 *   - 11px body text, 10px mono headers, monospace numbers
 *   - Consistent px-3 py-2 cell padding
 */

export interface EnterpriseColumn<T> {
  key: string;
  label: string;
  /** Render cell content. Defaults to `String(row[key])` */
  render?: (row: T, index: number) => React.ReactNode;
  /** Header alignment */
  align?: 'left' | 'center' | 'right';
  /** Column width hint (css) */
  width?: string;
  /** Use monospace font for this column (numbers, codes, dates) */
  mono?: boolean;
}

interface EnterpriseTableProps<T> {
  columns: EnterpriseColumn<T>[];
  data: T[];
  /** Unique key extractor */
  rowKey: (row: T) => string;
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  selectedKey?: string | null;
  onRowClick?: (row: T) => void;
  /** Optional footer left text (e.g. "12 registros") */
  footerText?: string;
  className?: string;
}

export function EnterpriseTable<T>({
  columns,
  data,
  rowKey,
  isLoading,
  emptyTitle = 'Nenhum registro encontrado',
  emptySubtitle,
  selectedKey,
  onRowClick,
  footerText,
  className = '',
}: EnterpriseTableProps<T>) {
  if (isLoading) {
    return (
      <div className="enterprise-table-shell">
        <table className="min-w-full table-fixed text-[11px]">
          <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
            <tr>
              {columns.map(col => (
                <th key={col.key} className="px-3 py-2 font-medium" style={col.width ? { width: col.width } : undefined}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="enterprise-table-row animate-pulse">
                <td colSpan={columns.length} className="px-3 py-2">
                  <div className="h-3 rounded-[2px] enterprise-surface-3" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="enterprise-card-surface flex min-h-[200px] flex-col items-center justify-center gap-1 px-6 text-center enterprise-text-secondary">
        <p className="font-mono text-[11px] uppercase tracking-[0.14em] enterprise-text-muted">{emptyTitle}</p>
        {emptySubtitle && <p className="text-xs enterprise-text-secondary">{emptySubtitle}</p>}
      </div>
    );
  }

  return (
    <div className={`enterprise-table-shell overflow-x-auto ${className}`.trim()}>
      <table className="min-w-full table-fixed text-[11px]">
        <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={`px-3 py-2 font-medium ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const key = rowKey(row);
            const isSelected = selectedKey === key;
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`enterprise-table-row ${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'is-selected' : ''}`}
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 align-middle ${col.mono ? 'font-mono' : ''} ${
                      colIdx === 0 && isSelected
                        ? 'relative pl-4 enterprise-text-primary font-semibold before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[#718198] before:shadow-[0_0_6px_rgba(113,129,152,0.28)]'
                        : colIdx === 0
                          ? 'enterprise-text-primary font-semibold'
                          : 'enterprise-text-secondary'
                    } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                  >
                    {col.render ? col.render(row, idx) : String((row as Record<string, unknown>)[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footerText && (
        <div className="border-t border-enterprise-border-strong px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] enterprise-text-muted">
          {footerText}
        </div>
      )}
    </div>
  );
}

/* ─── Badge helper — consistent status/severity pills ──── */

interface EnterpriseBadgeProps {
  label?: string;
  color?: string;
  className?: string;
  children?: React.ReactNode;
}

export function EnterpriseBadge({ label, color = '', className = '', children }: EnterpriseBadgeProps) {
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-semibold leading-tight ${color} ${className}`.trim()}>
      {children ?? label}
    </span>
  );
}

export default EnterpriseTable;
